import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import * as React from "react";
import { DesktopDropdown } from "./DesktopDropdown";
import { MobileSheet } from "./MobileSheet";
import type { MenuCtx, ResponsiveMenuPage } from "./types";

type ResponsiveMenuProps<T extends string = string> = {
  isMobile: boolean;
  trigger: React.ReactNode;

  /** Pages: include a "root" page */
  pages: ResponsiveMenuPage<T>[];

  /** Desktop dropdown positioning */
  desktopSide?: "top" | "right" | "bottom" | "left";
  desktopAlign?: "start" | "center" | "end";

  /** Mobile sheet side */
  sheetSide?: "top" | "right" | "bottom" | "left";
};

export function ResponsiveMenu<T extends string = string>({
  isMobile,
  trigger,
  pages,
  desktopSide = "right",
  desktopAlign = "end",
  sheetSide = "left",
}: ResponsiveMenuProps<T>) {
  const [open, setOpen] = React.useState(false);
  const pagesMap = React.useMemo(() => {
    const map = new Map<string, ResponsiveMenuPage<T>>();
    for (const p of pages) map.set(p.id, p);
    return map;
  }, [pages]);

  const [pageStack, setPageStack] = React.useState<string[]>(["root"]);

  const currentPageId = pageStack[pageStack.length - 1] ?? "root";
  const currentPage = pagesMap.get(currentPageId) ?? pagesMap.get("root");

  const close = React.useCallback(() => {
    setOpen(false);
    setPageStack(["root"]);
  }, []);

  const goTo = React.useCallback((pageId: string) => {
    setPageStack((s) => [...s, pageId]);
  }, []);

  const back = React.useCallback(() => {
    setPageStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const ctx: MenuCtx = React.useMemo(() => ({ close, goTo }), [close, goTo]);

  // Reset navigation when menu closes
  React.useEffect(() => {
    if (!open) setPageStack(["root"]);
  }, [open]);

  if (isMobile) {
    return (
      <>
        <div className="w-full" onClick={() => setOpen(true)}>
          {trigger}
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side={sheetSide} className="p-0">
            <MobileSheet
              page={currentPage}
              canGoBack={pageStack.length > 1}
              onBack={back}
              items={currentPage?.items ?? []}
              ctx={ctx}
            />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side={desktopSide}
        align={desktopAlign}
        sideOffset={4}
      >
        <DesktopDropdown
          page={pagesMap.get("root")}
          pagesMap={pagesMap}
          ctx={ctx}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
